import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface SourcePerformanceChartProps {
  data: Array<{
    source_name: string;
    total_leads: number;
    leads_converted: number;
    total_revenue: number;
    conversion_rate: number;
  }>;
}

export const SourcePerformanceChart = ({ data }: SourcePerformanceChartProps) => {
  const { formatMoney } = useCompanyLocale();
  const chartData = data.map(source => ({
    name: source.source_name,
    leads: source.total_leads,
    conversions: source.leads_converted,
    conversionRate: source.conversion_rate,
    revenue: source.total_revenue,
  }));

  const COLORS = {
    primary: 'hsl(var(--primary))',
    success: 'hsl(var(--success))',
    warning: 'hsl(var(--warning))',
    muted: 'hsl(var(--muted-foreground))',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lead Source Performance</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="name" 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
                color: 'hsl(var(--popover-foreground))',
              }}
              formatter={(value: number, name: string) => {
                if (name === 'conversionRate') return [`${value.toFixed(1)}%`, 'Conv. Rate'];
                if (name === 'revenue') return [formatMoney(value), 'Revenue'];
                return [value, name === 'leads' ? 'Total Leads' : 'Conversions'];
              }}
            />
            <Bar dataKey="leads" name="leads" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.conversionRate >= 50 ? COLORS.success : entry.conversionRate >= 25 ? COLORS.primary : COLORS.muted}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
